
import { 
    ref, set, update, push, get, onValue, remove, 
    query, orderByChild, equalTo, off
} from 'firebase/database';
import { db, auth } from './firebase';
import { handleDatabaseError, OperationType } from './databaseErrorHandler';
import { 
    AdminUser, StaffMember, ShuttleReservation, ParkingReservation, 
    TimeOffRequest, AttendanceRecord, Notification, Payment, Role, Vehicle,
    Department, Shift, PickupLocation, ReservationSource, ScheduleEntry, ClosedPeriod,
    NightAuditReport, Memo, MemoCategory, MemoTopic, MemoTag,
    TaskCategory, TaskTemplate, TaskInstance,
    MaintenanceRequest, DailyNightAuditReport, AccessRecord, AttendanceRequest
} from '../types';

/**
 * PRODUCTION-GRADE DATA ACCESS LAYER (REALTIME DATABASE)
 * 
 * Rules:
 * - NO direct database calls from UI
 * - All DB operations go through this layer
 * - Input validation before writes
 * - Error handling with specialized JSON format
 */

// Helper to clean objects for RTDB (remove undefined)
const cleanObject = (obj: any): any => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
        return obj.map(v => cleanObject(v)).filter(v => v !== null);
    }
    if (typeof obj === 'object') {
        const result: any = {};
        Object.keys(obj).forEach(key => {
            const value = cleanObject(obj[key]);
            if (value !== null) result[key] = value;
        });
        return result;
    }
    return obj;
};

export const dataAccess = {
    // --- USERS (Admins & Staff) ---
    async createUser(uid: string, data: Partial<AdminUser | StaffMember>) {
        const path = `users/${uid}`;
        try {
            const cleaned = cleanObject({
                ...data,
                createdAt: (data as any).createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                status: data.status || 'active'
            });
            await set(ref(db, path), cleaned);
            return { uid, ...cleaned };
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async getUser(uid: string) {
        const path = `users/${uid}`;
        try {
            const snapshot = await get(ref(db, path));
            return snapshot.exists() ? snapshot.val() : null;
        } catch (error) {
            handleDatabaseError(error, OperationType.GET, path);
        }
    },

    async getUserByPhone(phone: string) {
        try {
            const usersRef = ref(db, 'users');
            const phoneQuery = query(usersRef, orderByChild('phone'), equalTo(phone));
            const snapshot = await get(phoneQuery);
            
            if (snapshot.exists()) {
                const users = snapshot.val();
                const uids = Object.keys(users);
                if (uids.length > 0) {
                    return { uid: uids[0], ...users[uids[0]] };
                }
            }
            return null;
        } catch (error) {
            handleDatabaseError(error, OperationType.GET, 'users/phone');
        }
    },

    async updateUser(uid: string, data: Partial<AdminUser | StaffMember>) {
        const path = `users/${uid}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- RESERVATIONS ---
    async createReservation(data: Partial<ShuttleReservation | ParkingReservation> & { type: 'shuttle' | 'parking' }) {
        const pathPrefix = data.type === 'parking' ? 'parkingReservations' : 'shuttleReservations';
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: timestamp,
                updatedAt: timestamp,
                status: data.status || (data.type === 'parking' ? 'Reservation' : 'pending')
            });
            
            const newRef = push(ref(db, pathPrefix));
            const resId = newRef.key;
            const finalData = { ...cleaned, id: resId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, pathPrefix);
        }
    },

    async getReservation(resId: string, type?: 'shuttle' | 'parking') {
        try {
            if (type) {
                const path = type === 'parking' ? `parkingReservations/${resId}` : `shuttleReservations/${resId}`;
                const snapshot = await get(ref(db, path));
                return snapshot.exists() ? snapshot.val() : null;
            } else {
                // Try both
                const [shuttleSnap, parkingSnap] = await Promise.all([
                    get(ref(db, `shuttleReservations/${resId}`)),
                    get(ref(db, `parkingReservations/${resId}`))
                ]);
                if (shuttleSnap.exists()) return shuttleSnap.val();
                if (parkingSnap.exists()) return parkingSnap.val();
                return null;
            }
        } catch (error) {
            handleDatabaseError(error, OperationType.GET, `reservations/${resId}`);
        }
    },

    async updateReservationStatus(resId: string, status: string, type: 'shuttle' | 'parking') {
        const pathPrefix = type === 'parking' ? 'parkingReservations' : 'shuttleReservations';
        const path = `${pathPrefix}/${resId}`;
        try {
            await update(ref(db, path), {
                status,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async getReservationsByUser(userId: string) {
        try {
            const shuttleQuery = query(ref(db, 'shuttleReservations'), orderByChild('userId'), equalTo(userId));
            const parkingQuery = query(ref(db, 'parkingReservations'), orderByChild('userId'), equalTo(userId));
            
            const [shuttleSnap, parkingSnap] = await Promise.all([
                get(shuttleQuery),
                get(parkingQuery)
            ]);
            
            const shuttleRes = shuttleSnap.exists() ? Object.values(shuttleSnap.val()) : [];
            const parkingRes = parkingSnap.exists() ? Object.values(parkingSnap.val()) : [];
            
            return [...shuttleRes, ...parkingRes];
        } catch (error) {
            handleDatabaseError(error, OperationType.LIST, `reservations/user/${userId}`);
        }
    },

    // --- VEHICLES ---
    async createVehicle(data: Partial<Vehicle>) {
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: timestamp,
                updatedAt: timestamp,
                status: data.status || 'active'
            });
            
            const newRef = push(ref(db, 'vehicles'));
            const vehId = newRef.key;
            const finalData = { ...cleaned, id: vehId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'vehicles');
        }
    },

    async attachVehicleToUser(userId: string, vehicleId: string) {
        const path = `vehicles/${vehicleId}`;
        try {
            await update(ref(db, path), {
                userId,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- PAYMENTS ---
    async recordPayment(reservationId: string, data: Partial<Payment>) {
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                reservationId,
                createdAt: timestamp,
                updatedAt: timestamp,
                status: data.status || 'pending'
            });
            
            const newRef = push(ref(db, 'payments'));
            const payId = newRef.key;
            const finalData = { ...cleaned, id: payId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'payments');
        }
    },

    // --- ROLES ---
    async createRole(data: Partial<Role>) {
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: timestamp,
                updatedAt: timestamp
            });
            
            const newRef = push(ref(db, 'roles'));
            const roleId = newRef.key;
            const finalData = { ...cleaned, id: roleId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'roles');
        }
    },

    // --- TIME OFF REQUESTS ---
    async createTimeOffRequest(data: Partial<TimeOffRequest>) {
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: timestamp,
                updatedAt: timestamp,
                status: data.status || 'pending'
            });
            
            const newRef = push(ref(db, 'timeOffRequests'));
            const reqId = newRef.key;
            const finalData = { ...cleaned, id: reqId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'timeOffRequests');
        }
    },

    async updateTimeOffStatus(requestId: string, status: string) {
        const path = `timeOffRequests/${requestId}`;
        try {
            await update(ref(db, path), {
                status,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async updateTimeOffRequest(requestId: string, data: Partial<TimeOffRequest>) {
        const path = `timeOffRequests/${requestId}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- ATTENDANCE ---
    async recordAttendance(data: Partial<AttendanceRecord>) {
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: (data as any).createdAt || timestamp,
                updatedAt: timestamp
            });
            
            if (data.id) {
                await set(ref(db, `attendanceRecords/${data.id}`), cleaned);
                return cleaned;
            } else {
                const newRef = push(ref(db, 'attendanceRecords'));
                const recId = newRef.key;
                const finalData = { ...cleaned, id: recId };
                await set(newRef, finalData);
                return finalData;
            }
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, 'attendanceRecords');
        }
    },

    // --- NOTIFICATIONS ---
    async sendNotification(userId: string, data: Partial<Notification>) {
        try {
            const recordId = data.recordId || 'global';
            const eventType = data.eventType || 'info';
            const notificationId = `${userId}_${recordId}_${eventType}`;
            const notificationRef = ref(db, `notifications/${notificationId}`);

            // Check if notification already exists to prevent duplicates
            const snapshot = await get(notificationRef);
            if (snapshot.exists()) {
                // Optionally update timestamp or just return existing
                return snapshot.val();
            }

            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                userId,
                recordId,
                eventType,
                createdAt: timestamp,
                scheduledAt: (data as any).scheduledAt || timestamp,
                read: false
            });
            
            const finalData = { ...cleaned, id: notificationId };
            await set(notificationRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'notifications');
        }
    },

    async markNotificationRead(notificationId: string) {
        const path = `notifications/${notificationId}`;
        try {
            await update(ref(db, path), {
                read: true,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteNotification(notificationId: string) {
        const path = `notifications/${notificationId}`;
        try {
            await remove(ref(db, path));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    async clearNotifications(userId: string) {
        try {
            const notifQuery = query(ref(db, 'notifications'), orderByChild('userId'), equalTo(userId));
            const snapshot = await get(notifQuery);
            if (snapshot.exists()) {
                const updates: any = {};
                Object.keys(snapshot.val()).forEach(key => {
                    updates[`notifications/${key}`] = null;
                });
                await update(ref(db), updates);
            }
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `notifications/user/${userId}`);
        }
    },

    // --- CLOSED PERIODS ---
    async closePeriod(data: ClosedPeriod) {
        const path = `closedPeriods/${data.id}`;
        try {
            await set(ref(db, path), cleanObject(data));
            return data;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async getClosedPeriod(periodId: string) {
        const path = `closedPeriods/${periodId}`;
        try {
            const snapshot = await get(ref(db, path));
            return snapshot.exists() ? snapshot.val() as ClosedPeriod : null;
        } catch (error) {
            handleDatabaseError(error, OperationType.GET, path);
        }
    },

    // --- NIGHT AUDIT REPORTS ---
    async saveNightAuditReport(report: Partial<NightAuditReport>) {
        try {
            const timestamp = new Date().toISOString();
            const reportId = report.id || push(ref(db, 'nightAuditReports')).key;
            const cleaned = cleanObject({
                ...report,
                id: reportId,
                createdAt: timestamp,
                updatedAt: timestamp
            });

            await set(ref(db, `nightAuditReports/${reportId}`), cleaned);
            return cleaned;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, `nightAuditReports/${report.id}`);
        }
    },

    async saveActiveNightAudit(audit: Partial<NightAuditReport>) {
        try {
            if (!audit.id) return;
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...audit,
                updatedAt: timestamp
            });
            await set(ref(db, `activeNightAudits/${audit.id}`), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, `activeNightAudits/${audit.id}`);
        }
    },

    async deleteActiveNightAudit(auditId: string) {
        try {
            await set(ref(db, `activeNightAudits/${auditId}`), null);
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `activeNightAudits/${auditId}`);
        }
    },

    // --- SETTINGS & COLLECTIONS ---
    async saveSettings(path: string, data: any) {
        const dbPath = path === 'scheduleData' ? 'scheduleData' : `settings/${path}`;
        try {
            await update(ref(db, dbPath), cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            }));
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, dbPath);
        }
    },

    async updateStaffMember(staffId: string, data: Partial<StaffMember>) {
        const path = `scheduleData/staffList/${staffId}`;
        try {
            await update(ref(db, path), cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            }));
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async updateScheduleEntry(staffId: string, date: string, data: Partial<ScheduleEntry>) {
        const path = `scheduleData/schedule/${staffId}/${date}`;
        try {
            await update(ref(db, path), cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            }));
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- MEMO SYSTEM (Scalable Indexing) ---
    async createMemo(memo: Omit<Memo, 'id' | 'createdAt' | 'updatedAt'>) {
        try {
            const memoRef = push(ref(db, 'memos'));
            const memoId = memoRef.key!;
            const timestamp = new Date().toISOString();
            
            const finalMemo: Memo = {
                ...memo,
                id: memoId,
                createdAt: timestamp,
                updatedAt: timestamp
            };

            const updates: any = {};
            updates[`memos/${memoId}`] = finalMemo;
            
            // Indexing
            if (finalMemo.categoryId) {
                updates[`index_category_memos/${finalMemo.categoryId}/${memoId}`] = true;
            }
            if (finalMemo.topicId) {
                updates[`index_topic_memos/${finalMemo.topicId}/${memoId}`] = true;
            }
            if (finalMemo.tags) {
                Object.keys(finalMemo.tags).forEach(tagId => {
                    updates[`index_tag_memos/${tagId}/${memoId}`] = true;
                });
            }

            await update(ref(db), updates);
            return finalMemo;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'memos');
        }
    },

    async updateMemo(memoId: string, data: Partial<Memo>, oldMemo?: Memo) {
        try {
            const timestamp = new Date().toISOString();
            const updates: any = {};
            
            const updatedMemo = {
                ...data,
                updatedAt: timestamp
            };

            updates[`memos/${memoId}`] = updatedMemo;

            // Handle Index Updates if oldMemo is provided
            if (oldMemo) {
                // Category change
                if (data.categoryId !== undefined && data.categoryId !== oldMemo.categoryId) {
                    if (oldMemo.categoryId) updates[`index_category_memos/${oldMemo.categoryId}/${memoId}`] = null;
                    if (data.categoryId) updates[`index_category_memos/${data.categoryId}/${memoId}`] = true;
                }
                // Topic change
                if (data.topicId !== undefined && data.topicId !== oldMemo.topicId) {
                    if (oldMemo.topicId) updates[`index_topic_memos/${oldMemo.topicId}/${memoId}`] = null;
                    if (data.topicId) updates[`index_topic_memos/${data.topicId}/${memoId}`] = true;
                }
                // Tags change
                if (data.tags !== undefined) {
                    // Remove old tags
                    if (oldMemo.tags) {
                        Object.keys(oldMemo.tags).forEach(tagId => {
                            if (!data.tags![tagId]) {
                                updates[`index_tag_memos/${tagId}/${memoId}`] = null;
                            }
                        });
                    }
                    // Add new tags
                    Object.keys(data.tags).forEach(tagId => {
                        if (!oldMemo.tags || !oldMemo.tags[tagId]) {
                            updates[`index_tag_memos/${tagId}/${memoId}`] = true;
                        }
                    });
                }
            }

            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, `memos/${memoId}`);
        }
    },

    async deleteMemo(memoId: string, memo: Memo) {
        try {
            const updates: any = {};
            // Soft delete
            updates[`memos/${memoId}/status`] = 'deleted';
            updates[`memos/${memoId}/updatedAt`] = new Date().toISOString();
            
            // Remove from indexes so it doesn't show up in filtered lists
            if (memo.categoryId) updates[`index_category_memos/${memo.categoryId}/${memoId}`] = null;
            if (memo.topicId) updates[`index_topic_memos/${memo.topicId}/${memoId}`] = null;
            if (memo.tags) {
                Object.keys(memo.tags).forEach(tagId => {
                    updates[`index_tag_memos/${tagId}/${memoId}`] = null;
                });
            }

            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `memos/${memoId}`);
        }
    },

    async createMemoCategory(name: string, color: string) {
        try {
            const categoryRef = push(ref(db, 'categories'));
            const id = categoryRef.key!;
            const category: MemoCategory = {
                id,
                name,
                color,
                status: 'active',
                createdAt: new Date().toISOString()
            };
            await set(categoryRef, category);
            return category;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'categories');
        }
    },

    async createMemoTopic(name: string, categoryId: string) {
        try {
            const topicRef = push(ref(db, 'topics'));
            const id = topicRef.key!;
            const topic: MemoTopic = {
                id,
                name,
                categoryId,
                status: 'active',
                createdAt: new Date().toISOString()
            };
            await set(topicRef, topic);
            return topic;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'topics');
        }
    },

    async createMemoTag(name: string) {
        try {
            const tagRef = push(ref(db, 'tags'));
            const id = tagRef.key!;
            const tag: MemoTag = {
                id,
                name,
                status: 'active',
                createdAt: new Date().toISOString()
            };
            await set(tagRef, tag);
            return tag;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, 'tags');
        }
    },

    async deleteMemoCategory(id: string) {
        try {
            await remove(ref(db, `categories/${id}`));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `categories/${id}`);
        }
    },

    async deleteMemoTopic(id: string) {
        try {
            await remove(ref(db, `topics/${id}`));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `topics/${id}`);
        }
    },

    async deleteMemoTag(id: string) {
        try {
            await remove(ref(db, `tags/${id}`));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, `tags/${id}`);
        }
    },

    async updateWeeklyMetric(date: string, field: string, value: number) {
        const path = `weeklyMetrics/${date}/${field}`;
        try {
            const updates: any = {};
            updates[path] = value;
            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- TASK MODULE ---
    async createTaskCategory(category: Partial<TaskCategory>) {
        const id = push(ref(db, 'taskCategories')).key || '';
        const path = `taskCategories/${id}`;
        try {
            const cleaned = cleanObject({
                ...category,
                id,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            await set(ref(db, path), cleaned);
            return cleaned;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async updateTaskCategory(id: string, data: Partial<TaskCategory>) {
        const path = `taskCategories/${id}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteTaskCategory(id: string) {
        const path = `taskCategories/${id}`;
        try {
            const updates: any = {};
            updates[path] = null;
            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    async createTaskTemplate(template: Partial<TaskTemplate>) {
        const id = push(ref(db, 'taskTemplates')).key || '';
        const path = `taskTemplates/${id}`;
        try {
            const cleaned = cleanObject({
                ...template,
                id,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            await set(ref(db, path), cleaned);
            return cleaned;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async updateTaskTemplate(id: string, data: Partial<TaskTemplate>) {
        const path = `taskTemplates/${id}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteTaskTemplate(id: string) {
        const path = `taskTemplates/${id}`;
        try {
            const updates: any = {};
            updates[path] = null;
            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    async createTaskInstance(instance: Partial<TaskInstance>) {
        const id = push(ref(db, 'taskInstances')).key || '';
        const path = `taskInstances/${id}`;
        try {
            const cleaned = cleanObject({
                ...instance,
                id,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            await set(ref(db, path), cleaned);
            return cleaned;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async updateTaskInstance(id: string, data: Partial<TaskInstance>) {
        const path = `taskInstances/${id}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteTaskInstance(id: string) {
        const path = `taskInstances/${id}`;
        try {
            const updates: any = {};
            updates[path] = null;
            await update(ref(db), updates);
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    // --- MAINTENANCE MODULE ---
    async createMaintenanceRequest(data: Partial<MaintenanceRequest>) {
        const id = push(ref(db, 'maintenanceRequests')).key || '';
        const path = `maintenanceRequests/${id}`;
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                id,
                createdAt: timestamp,
                updatedAt: timestamp,
                status: data.status || 'Open',
                history: [
                    {
                        action: 'Created',
                        status: data.status || 'Open',
                        changedBy: data.createdBy || 'Unknown',
                        timestamp: timestamp,
                        note: ''
                    }
                ]
            });
            await set(ref(db, path), cleaned);
            return cleaned;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async updateMaintenanceRequest(id: string, data: Partial<MaintenanceRequest>) {
        const path = `maintenanceRequests/${id}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteMaintenanceRequest(id: string) {
        const path = `maintenanceRequests/${id}`;
        try {
            await remove(ref(db, path));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    // --- DAILY NIGHT AUDIT REPORTS ---
    async saveDailyNightAuditReport(report: Partial<DailyNightAuditReport>) {
        const id = report.id || push(ref(db, 'dailyNightAuditReports')).key || '';
        const path = `dailyNightAuditReports/${id}`;
        try {
            const cleaned = cleanObject({
                ...report,
                id,
                createdAt: report.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            await set(ref(db, path), cleaned);
            return cleaned as DailyNightAuditReport;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async deleteDailyNightAuditReport(id: string) {
        const path = `dailyNightAuditReports/${id}`;
        try {
            await remove(ref(db, path));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    // --- ACCESS MANAGER ---
    async createAccessRecord(data: Partial<AccessRecord>) {
        const id = push(ref(db, 'accessRecords')).key || '';
        const path = `accessRecords/${id}`;
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                id,
                createdAt: timestamp,
                updatedAt: timestamp
            });
            await set(ref(db, path), cleaned);
            return cleaned as AccessRecord;
        } catch (error) {
            handleDatabaseError(error, OperationType.WRITE, path);
        }
    },

    async updateAccessRecord(id: string, data: Partial<AccessRecord>) {
        const path = `accessRecords/${id}`;
        try {
            const cleaned = cleanObject({
                ...data,
                updatedAt: new Date().toISOString()
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    async deleteAccessRecord(id: string) {
        const path = `accessRecords/${id}`;
        try {
            await remove(ref(db, path));
        } catch (error) {
            handleDatabaseError(error, OperationType.DELETE, path);
        }
    },

    // --- ATTENDANCE REQUESTS ---
    async createAttendanceRequest(data: Partial<AttendanceRequest>) {
        const pathPrefix = 'attendanceRequests';
        try {
            const timestamp = new Date().toISOString();
            const cleaned = cleanObject({
                ...data,
                createdAt: timestamp,
                status: 'PENDING'
            });
            
            const newRef = push(ref(db, pathPrefix));
            const reqId = newRef.key;
            const finalData = { ...cleaned, id: reqId };
            await set(newRef, finalData);
            
            return finalData;
        } catch (error) {
            handleDatabaseError(error, OperationType.CREATE, pathPrefix);
        }
    },

    async updateAttendanceRequest(requestId: string, data: Partial<AttendanceRequest>) {
        const path = `attendanceRequests/${requestId}`;
        try {
            const cleaned = cleanObject({
                ...data,
                reviewedAt: data.status !== 'PENDING' ? new Date().toISOString() : undefined
            });
            await update(ref(db, path), cleaned);
        } catch (error) {
            handleDatabaseError(error, OperationType.UPDATE, path);
        }
    },

    // --- GENERIC SUBSCRIPTION ---
    subscribe(path: string, callback: (data: any) => void, onError?: (error: any) => void) {
        const dbRef = ref(db, path);
        const unsubscribe = onValue(dbRef, (snapshot) => {
            callback(snapshot.val());
        }, (error) => {
            if (onError) onError(error);
            handleDatabaseError(error, OperationType.GET, path);
        });
        
        return () => off(dbRef, 'value', unsubscribe);
    },

    subscribeToQuery(queryRef: any, callback: (data: any) => void, onError?: (error: any) => void) {
        const unsubscribe = onValue(queryRef, (snapshot) => {
            callback(snapshot.val());
        }, (error) => {
            if (onError) onError(error);
            handleDatabaseError(error, OperationType.GET, 'query');
        });
        
        return () => off(queryRef, 'value', unsubscribe);
    }
};
